import React, { useState, useEffect, useCallback } from 'react';
import {
  hotelsApi, usersApi, roomTypesApi, roomsApi, billSettingsApi
} from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useConfirm, useNotify } from '../../context/ConfirmContext';


// ─── Sub-tab IDs ─────────────────────────────────────────────────────────────
const SUB_TABS = [
  { id: 'details', icon: '🏢', label: 'Hotel Details' },
  { id: 'users', icon: '🔑', label: 'User Access' },
  { id: 'roomtypes', icon: '🏷️', label: 'Room Types' },
  { id: 'rooms', icon: '🛏️', label: 'Room Setup' },
  { id: 'bill', icon: '🖨️', label: 'Bill Designer' },
];

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function HotelManagementPage({ onSuccess }) {
  const { user, hotel: currentHotel, updateHotel } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin';

  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);

  // ── load hotel list ──────────────────────────────────────────────────────
  const loadHotels = useCallback(async () => {
    setLoading(true);
    try {
      const all = await hotelsApi.list();
      setHotels(all);
      // Only set initial selection if none exists
      setSelected(current => {
        if (!current && all.length > 0) return all[0];
        return current;
      });
    } catch (err) {
      console.error('Failed to load hotels:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadHotels(); }, [loadHotels]);

  // When hotel list changes, keep selectedHotel in sync. Deliberately not
  // depending on selectedHotel itself — this effect calls setSelected, so doing
  // so would risk the effect re-triggering itself on every refresh.
  useEffect(() => {
    if (selectedHotel) {
      const fresh = hotels.find(h => h.id === selectedHotel.id);
      if (fresh) setSelected(fresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels]);

  const handleHotelSaved = (h) => {
    loadHotels();
    setSelected(h);
    if (h.id === currentHotel?.id) updateHotel(h);
    onSuccess?.(`${h.name} saved!`);
  };

  if (loading) return <div className="panel" style={{ padding: 60, textAlign: 'center' }}>Loading…</div>;

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', height: '100%' }}>

      {/* ── LEFT: Hotel list ─────────────────────────────────────────── */}
      <div style={{ width: 230, flexShrink: 0 }}>
        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', color: 'var(--muted)', letterSpacing: 0.5 }}>Properties</span>
            {isSuperAdmin && (
              <button className="btn btn-gold btn-sm" style={{ fontSize: 11, padding: '3px 10px' }}
                onClick={() => { setSelected('new'); setActiveTab('details'); }}>
                + Add
              </button>
            )}
          </div>
          <div>
            {hotels.map(h => (
              <div
                key={h.id}
                onClick={() => { setSelected(h); setActiveTab('details'); }}
                style={{
                  padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
                  background: selectedHotel?.id === h.id ? 'rgba(200,151,42,0.10)' : 'transparent',
                  borderLeft: `3px solid ${selectedHotel?.id === h.id ? h.color || 'var(--gold)' : 'transparent'}`,
                  transition: 'all 0.15s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: h.color || '#c8972a', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 12 }}>{h.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{h.city} · {h.floors || 1}F</div>
                  </div>
                </div>
              </div>
            ))}
            {hotels.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--muted)', textAlign: 'center' }}>No hotels yet.<br />Click + Add to create one.</div>
            )}
          </div>
        </div>
      </div>

      {/* ── RIGHT: Sub-tab content ───────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        {!selectedHotel ? (
          <div className="panel" style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🏨</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Select a hotel from the list to manage it</div>
          </div>
        ) : (
          <>
            {/* Sub-tab bar */}
            {selectedHotel !== 'new' && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: 'var(--s1)', borderRadius: 12, padding: 6, border: '1px solid var(--border)' }}>
                {SUB_TABS.map(t => (
                  <button
                    key={t.id}
                    onClick={() => setActiveTab(t.id)}
                    style={{
                      flex: 1, padding: '8px 4px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      background: activeTab === t.id ? 'var(--bg)' : 'transparent',
                      color: activeTab === t.id ? 'var(--gold)' : 'var(--muted)',
                      boxShadow: activeTab === t.id ? '0 1px 6px rgba(0,0,0,0.2)' : 'none',
                      transition: 'all 0.15s',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            )}

            {/* Sub-tab panels */}
            {(selectedHotel === 'new' || activeTab === 'details') && (
              <HotelDetailsTab
                hotel={selectedHotel === 'new' ? null : selectedHotel}
                isSuperAdmin={isSuperAdmin}
                onSaved={handleHotelSaved}
                onCancel={() => setSelected(hotels[0] || null)}
                onDeleted={(id) => {
                  loadHotels();
                  setSelected(null); // Deselect explicitly
                  onSuccess?.('Hotel deleted!');
                }}
              />
            )}
            {selectedHotel !== 'new' && activeTab === 'users' && (
              <UsersTab hotel={selectedHotel} onSuccess={onSuccess} />
            )}
            {selectedHotel !== 'new' && activeTab === 'roomtypes' && (
              <RoomTypesTab hotel={selectedHotel} onSuccess={onSuccess} />
            )}
            {selectedHotel !== 'new' && activeTab === 'rooms' && (
              <RoomsTab hotel={selectedHotel} onSuccess={onSuccess} />
            )}
            {selectedHotel !== 'new' && activeTab === 'bill' && (
              <BillDesignerTab hotel={selectedHotel} onSuccess={onSuccess} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
//  HOTEL DETAILS TAB
// ═══════════════════════════════════════════════════════
function HotelDetailsTab({ hotel, isSuperAdmin, onSaved, onCancel, onDeleted }) {
  const notify = useNotify();
  const isNew = !hotel;
  const [form, setForm] = useState({
    hotelCode: hotel?.hotel_code || hotel?.hotelCode || '',
    name: hotel?.name || '',
    city: hotel?.city || '',
    address: hotel?.address || '',
    phone: hotel?.phone || '',
    email: hotel?.email || '',
    gstNumber: hotel?.gst_number || hotel?.gstNumber || '',
    gstName: hotel?.gst_name || hotel?.gstName || '',
    color: hotel?.color || '#e8b86d',
    floors: hotel?.floors || 3,
    tag: hotel?.tag || 'Hotel',
    gstRate: hotel?.gst_rate || hotel?.gstRate || 5,
    gstPrefix: hotel?.gst_prefix || hotel?.gstPrefix || '',
    ngstPrefix: hotel?.ngst_prefix || hotel?.ngstPrefix || '',
    startingInvoiceNumber: hotel?.starting_invoice_number || hotel?.startingInvoiceNumber || 1,
    lateCheckoutGraceMins: hotel?.late_checkout_grace_mins ?? hotel?.lateCheckoutGraceMins ?? 30,
    lateCheckoutHourlyRate: hotel?.late_checkout_hourly_rate ?? hotel?.lateCheckoutHourlyRate ?? 200,
    lateCheckoutHalfDayHours: hotel?.late_checkout_half_day_hours ?? hotel?.lateCheckoutHalfDayHours ?? 4,
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Reload the form only when the selected hotel's id changes, not on every
  // render with a new (but logically identical) hotel object reference —
  // depending on the full object would reset in-progress edits.
  useEffect(() => {
    if (hotel) {
      setForm({
        hotelCode: hotel.hotel_code || hotel.hotelCode || '',
        name: hotel.name || '',
        city: hotel.city || '',
        address: hotel.address || '',
        phone: hotel.phone || '',
        email: hotel.email || '',
        gstNumber: hotel.gst_number || hotel.gstNumber || '',
        gstName: hotel.gst_name || hotel.gstName || '',
        color: hotel.color || '#e8b86d',
        floors: hotel.floors || 3,
        tag: hotel.tag || 'Hotel',
        gstRate: hotel.gst_rate || hotel.gstRate || 5,
        gstPrefix: hotel.gst_prefix || hotel.gstPrefix || '',
        ngstPrefix: hotel.ngst_prefix || hotel.ngstPrefix || '',
        startingInvoiceNumber: hotel.starting_invoice_number || hotel.startingInvoiceNumber || 1,
        lateCheckoutGraceMins: hotel.late_checkout_grace_mins ?? hotel.lateCheckoutGraceMins ?? 30,
        lateCheckoutHourlyRate: hotel.late_checkout_hourly_rate ?? hotel.lateCheckoutHourlyRate ?? 200,
        lateCheckoutHalfDayHours: hotel.late_checkout_half_day_hours ?? hotel.lateCheckoutHalfDayHours ?? 4,
      });
    } else {
      setForm({
        hotelCode: '', name: '', city: '', address: '', phone: '', email: '',
        gstNumber: '', gstName: '', color: '#e8b86d', floors: 3, tag: 'Hotel', gstRate: 5,
        gstPrefix: '', ngstPrefix: '', startingInvoiceNumber: 1,
        lateCheckoutGraceMins: 30, lateCheckoutHourlyRate: 200, lateCheckoutHalfDayHours: 4,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) return;

    // Item 1: Phone number validation (10 digits)
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      await notify('Phone number must contain exactly 10 digits.');
      return;
    }

    // Item 2: Email format validation
    if (form.email && form.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.email.trim())) {
        await notify('Please enter a valid email address.');
        return;
      }
    }

    // Item 3: GST validation (15 alphanumeric characters)
    if (form.gstNumber && form.gstNumber.trim()) {
      const cleanGst = form.gstNumber.replace(/[^A-Z0-9]/gi, '').toUpperCase();
      if (cleanGst.length !== 15) {
        await notify('GST Number must contain exactly 15 alphanumeric characters with no special symbols.');
        return;
      }
    }

    setSaving(true);
    try {
      let result;
      const payload = {
        ...form,
        phone: phoneDigits,
        email: form.email ? form.email.trim() : '',
        gstNumber: form.gstNumber ? form.gstNumber.replace(/[^A-Z0-9]/gi, '').toUpperCase() : '',
      };
      if (isNew) {
        result = await hotelsApi.create(payload);
      } else {
        result = await hotelsApi.update(hotel.id, payload);
      }
      onSaved(result);
    } catch (err) { await notify(err.message); }
    setSaving(false);
  };

  const executeDelete = async () => {
    setSaving(true);
    try {
      await hotelsApi.delete(hotel.id);
      onDeleted(hotel.id);
    } catch (err) { await notify(err.message); }
    setSaving(false);
    setConfirmDelete(false);
  };

  const handleDelete = () => {
    setConfirmDelete(true);
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">{isNew ? '➕ Add New Hotel' : `✏️ ${hotel.name}`}</span>
        {isNew && <button className="btn btn-ghost btn-sm" onClick={onCancel}>← Cancel</button>}
      </div>
      <form onSubmit={handleSave} style={{ padding: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="field"><label>Hotel Code * (3 letters)</label>
            <input required maxLength={3} value={form.hotelCode} onChange={e => set('hotelCode', e.target.value.toUpperCase())} placeholder="MER" disabled={!isSuperAdmin} />
          </div>
          <div className="field"><label>Hotel Name *</label>
            <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="The Meridian" disabled={!isSuperAdmin} />
          </div>
        </div>
        <div className="field" style={{ marginBottom: 20 }}>
          <label>Full Address *</label>
          <input required value={form.address} onChange={e => set('address', e.target.value)} placeholder="14, Marine Drive, Mumbai..." />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="field"><label>City *</label>
            <input required value={form.city} onChange={e => set('city', e.target.value)} placeholder="Mumbai" />
          </div>
          <div className="field"><label>Category / Tag</label>
            <input value={form.tag} onChange={e => set('tag', e.target.value)} placeholder="5-Star Luxury" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="field"><label>Phone * (10 digits only)</label>
            <input required maxLength={10} value={form.phone} onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9876543210" />
          </div>
          <div className="field"><label>Email</label>
            <input type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="info@hotel.com" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="field"><label>GST Number (15 alphanumeric characters only)</label>
            <input maxLength={15} value={form.gstNumber} onChange={e => set('gstNumber', e.target.value.replace(/[^A-Z0-9]/gi, '').toUpperCase().slice(0, 15))} placeholder="27AAAAA0000A1Z5" />
          </div>
          <div className="field"><label>GST Registered Name</label>
            <input value={form.gstName} onChange={e => set('gstName', e.target.value)} placeholder="Hotels Pvt Ltd" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div className="field">
            <label>GST Invoice Prefix (e.g. Med/26/)</label>
            <input value={form.gstPrefix} onChange={e => set('gstPrefix', e.target.value)} placeholder="Med/26/GST/" />
          </div>
          <div className="field">
            <label>Non-GST Invoice Prefix (e.g. Med/26/)</label>
            <input value={form.ngstPrefix} onChange={e => set('ngstPrefix', e.target.value)} placeholder="Med/26/" />
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 30 }}>
          <div className="field">
            <label>Starting Inv #</label>
            <input type="number" min="1" value={form.startingInvoiceNumber} onChange={e => set('startingInvoiceNumber', Number(e.target.value))} />
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Example: 101</div>
          </div>
          <div className="field">
            <label>GST Rate (%)</label>
            <input type="number" min="0" max="28" value={form.gstRate} onChange={e => set('gstRate', Number(e.target.value))} />
            <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Default: 5% (India hotel GST)</div>
          </div>
          <div className="field">
            <label>Number of Floors</label>
            <input type="number" min="1" max="50" value={form.floors} onChange={e => set('floors', Number(e.target.value))} />
          </div>
          <div className="field">
            <label>Theme Color</label>
            <input type="color" value={form.color} onChange={e => set('color', e.target.value)} style={{ height: 42, padding: 2, width: '100%' }} />
          </div>
        </div>

        {/* LATE CHECKOUT FEE RULES */}
        <div style={{ background: 'var(--s1)', padding: 18, borderRadius: 12, border: '1px solid var(--border)', marginBottom: 30 }}>
          <h4 style={{ margin: '0 0 12px 0', fontSize: 14, color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}>
            ⏰ Automated Late Checkout Fee Rules
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
            <div className="field">
              <label>Grace Period (Minutes)</label>
              <input type="number" min="0" max="180" value={form.lateCheckoutGraceMins} onChange={e => set('lateCheckoutGraceMins', Number(e.target.value))} />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Grace time after 12:00 PM cutoff</div>
            </div>
            <div className="field">
              <label>Hourly Late Rate (₹/hr)</label>
              <input type="number" min="0" value={form.lateCheckoutHourlyRate} onChange={e => set('lateCheckoutHourlyRate', Number(e.target.value))} />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Auto-added per hour late</div>
            </div>
            <div className="field">
              <label>Half-Day Threshold (Hours)</label>
              <input type="number" min="1" max="12" value={form.lateCheckoutHalfDayHours} onChange={e => set('lateCheckoutHalfDayHours', Number(e.target.value))} />
              <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Hours late before half-day rate</div>
            </div>
          </div>
        </div>
        {isSuperAdmin && (
          <div style={{ display: 'flex', gap: 16 }}>
            <button className="btn btn-gold" type="submit" disabled={saving} style={{ flex: 1, height: 46 }}>
              {saving ? '⌛ Saving…' : (isNew ? '🏨 Create Hotel' : '✅ Save Changes')}
            </button>
            {!isNew && (
              <button type="button" onClick={handleDelete} disabled={saving} style={{ background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 8, padding: '0 24px', fontWeight: 700, cursor: 'pointer', height: 46 }}>
                🗑️ Delete Hotel
              </button>
            )}
          </div>
        )}
        {!isSuperAdmin && (
          <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: 10 }}>Only Super Admin can edit hotel details.</div>
        )}
      </form>

      {/* CUSTOM DELETE CONFIRMATION MODAL */}
      {confirmDelete && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, 
          background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(2px)', 
          zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{ background: 'var(--bg)', padding: 30, borderRadius: 12, width: 400, textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', border: '1px solid var(--border)' }}>
            <h3 style={{ color: 'var(--danger)', marginBottom: 12, fontSize: 20 }}>⚠️ Confirm Deletion</h3>
            <p style={{ marginBottom: 24, fontSize: 14, color: 'var(--muted)', lineHeight: 1.5 }}>
              Are you sure you want to completely delete <strong>{hotel.name}</strong> and all its data? This action is permanent and cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="button" className="btn btn-ghost" style={{ flex: 1, padding: '12px 0' }} onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button type="button" className="btn" style={{ flex: 1, background: 'var(--danger)', color: '#fff', border: 'none', padding: '12px 0' }} onClick={executeDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
//  USERS TAB
// ═══════════════════════════════════════════════════════
function UsersTab({ hotel, onSuccess }) {
  const { user: authUser } = useAuth();
  const confirm = useConfirm();
  const notify = useNotify();
  const isSuperAdmin = authUser?.role === 'superadmin';
  const [users, setUsers] = useState([]);
  const [allHotels, setAllHotels] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const blankForm = { username: '', name: '', password: '', hotelId: hotel.id, role: 'staff', shift: 'Morning' };
  const [form, setForm] = useState(blankForm);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    try {
      const [us, hs] = await Promise.all([usersApi.list(), hotelsApi.list()]);
      const mapped = us.map(u => ({ ...u, hotelId: u.hotel_id }));
      setUsers(isSuperAdmin ? mapped.filter(u => !u.hotelId || u.hotelId === hotel.id) : mapped.filter(u => u.hotelId === hotel.id));
      setAllHotels(hs);
    } catch (err) { console.error(err); }
  }, [hotel.id, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === 'new') {
        await usersApi.create({ ...form, hotelId: form.role === 'superadmin' ? null : form.hotelId });
      } else {
        await usersApi.update(editing, { ...form, hotelId: form.role === 'superadmin' ? null : form.hotelId });
      }
      onSuccess?.(`User saved!`);
      setEditing(null);
      load();
    } catch (err) { await notify(err.message); }
    setSaving(false);
  };

  const handleDelete = async (u) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete User', body: `Delete user ${u.name}?`, confirmText: 'Delete', danger: true })) return;
    try {
      await usersApi.delete(u.id);
      onSuccess?.(`User ${u.name} deleted successfully.`);
      load();
    } catch (err) { await notify(err.message); }
  };

  if (editing !== null) {
    return (
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">{editing === 'new' ? '➕ New User' : '✏️ Edit User'}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>← Back</button>
        </div>
        <form onSubmit={handleSave} style={{ padding: 24 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="field"><label>Full Name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Kiran Nair" />
            </div>
            <div className="field"><label>Username *</label>
              <input required value={form.username} onChange={e => set('username', e.target.value)} disabled={editing !== 'new'} placeholder="kiran.nair" />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="field"><label>{editing === 'new' ? 'Password *' : 'New Password (leave blank to keep)'}</label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)} required={editing === 'new'} placeholder="Min 6 characters" />
            </div>
            <div className="field"><label>Role *</label>
              <select value={form.role} onChange={e => set('role', e.target.value)}>
                {isSuperAdmin && <option value="superadmin">Super Admin</option>}
                <option value="manager">Manager</option>
                <option value="staff">Staff</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div className="field"><label>Assigned Hotel</label>
              <select value={form.hotelId || ''} onChange={e => set('hotelId', e.target.value)} disabled={form.role === 'superadmin'}>
                <option value="">— All Hotels (superadmin) —</option>
                {allHotels.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Default Shift</label>
              <select value={form.shift} onChange={e => set('shift', e.target.value)}>
                <option>Morning</option>
                <option>Evening</option>
                <option>Night</option>
              </select>
            </div>
          </div>
          <button className="btn btn-gold" type="submit" disabled={saving} style={{ width: '100%', height: 44 }}>
            {saving ? '⌛ Saving…' : '✅ Save User'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">👥 User Access — {hotel.name}</span>
        <button className="btn btn-gold btn-sm" onClick={() => { setForm({ ...blankForm, hotelId: hotel.id }); setEditing('new'); }}>+ Add User</button>
      </div>
      <div style={{ padding: 16 }}>
        {users.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No users assigned to this hotel yet.</div>}
        <div style={{ display: 'grid', gap: 10 }}>
          {users.map(u => (
            <div key={u.id} style={{ background: 'var(--s1)', borderRadius: 10, padding: '12px 16px', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: u.role === 'superadmin' ? 'var(--gold)' : u.role === 'manager' ? 'var(--teal)' : 'var(--s2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>
                  {u.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13 }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>@{u.username} · {u.shift || 'Morning'} Shift</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className={`badge ${u.role === 'superadmin' ? 'b-gold' : u.role === 'manager' ? 'b-teal' : 'b-blue'}`}>{u.role}</span>
                <button className="btn btn-ghost btn-sm" onClick={() => { setForm({ ...u, password: '' }); setEditing(u.id); }}>✏️</button>
                {isSuperAdmin && u.id !== authUser?.id && (
                  <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(u)}>🗑️</button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
//  ROOM TYPES TAB
// ═══════════════════════════════════════════════════════
function RoomTypesTab({ hotel, onSuccess }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [types, setTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const blank = { name: '', basePrice: '', baseAdults: 2, maxAdults: 3, extraBedPrice: '', baseChildren: 0, totalRooms: 1, color: '#6db5e8' };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setError(null);
    try {
      const ts = await roomTypesApi.list(hotel.id);
      if (!Array.isArray(ts)) throw new Error('Invalid room types data received');
      const mapped = ts.map(t => ({
        ...t,
        basePrice: t.base_price,
        baseAdults: t.base_adults,
        baseChildren: t.base_children,
        maxAdults: t.max_adults,
        extraBedPrice: t.extra_bed_price,
        totalRooms: t.total_rooms,
        color: t.color || '#6db5e8'
      }));
      setTypes(mapped);
    } catch (err) { 
      console.error(err); 
      setError(err.message);
    }
  }, [hotel.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...form,
        hotelId: hotel.id,
        basePrice: Number(form.basePrice) || 0,
        extraBedPrice: Number(form.extraBedPrice) || 0,
        baseAdults: Number(form.baseAdults) || 2,
        baseChildren: Number(form.baseChildren) || 0,
        maxAdults: Number(form.maxAdults) || 3,
        totalRooms: Number(form.totalRooms) || 1
      };
      if (editing === 'new') {
        await roomTypesApi.create(data);
      } else {
        await roomTypesApi.update(editing, data);
      }
      onSuccess?.(`Room type saved!`);
      setEditing(null);
      load();
    } catch (err) { await notify(err.message); }
    setSaving(false);
  };

  const handleDelete = async (rt) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete Room Type', body: `Delete room type "${rt.name}"?`, confirmText: 'Delete', danger: true })) return;
    try {
      await roomTypesApi.delete(rt.id);
      load();
    } catch (err) { await notify(err.message); }
  };

  if (editing !== null) {
    return (
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">{editing === 'new' ? '➕ New Room Type' : '✏️ Edit Room Type'}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>← Back</button>
        </div>
        <form onSubmit={handleSave} style={{ padding: 24 }}>
          <div className="field" style={{ marginBottom: 16 }}><label>Room Type Name *</label>
            <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="Deluxe King" />
          </div>
          {/* Color picker */}
          <div className="field" style={{ marginBottom: 16 }}>
            <label>Room Type Color <span style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 400 }}>(used on dashboard room cards)</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <input
                type="color"
                value={form.color || '#6db5e8'}
                onChange={e => set('color', e.target.value)}
                style={{ width: 48, height: 38, padding: 2, border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', background: 'none' }}
              />
              <input
                type="text"
                value={form.color || '#6db5e8'}
                onChange={e => set('color', e.target.value)}
                maxLength={7}
                placeholder="#6db5e8"
                style={{ flex: 1, fontFamily: 'monospace', letterSpacing: '0.05em' }}
              />
              <div style={{ display: 'flex', gap: 6 }}>
                {['#e8b86d','#7ec8c8','#e87d7d','#6db5e8','#a8e86d','#c87ec8','#9b8be8','#e8c86d'].map(c => (
                  <div key={c} onClick={() => set('color', c)} style={{ width: 24, height: 24, borderRadius: 6, background: c, cursor: 'pointer', border: form.color === c ? '2px solid #000' : '2px solid transparent', flexShrink: 0 }} title={c} />
                ))}
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="field"><label>Base Price ₹ / night *</label>
              <input type="number" required min="0" value={form.basePrice} onChange={e => set('basePrice', e.target.value)} />
            </div>
            <div className="field"><label>Extra Bed Price ₹</label>
              <input type="number" min="0" value={form.extraBedPrice} onChange={e => set('extraBedPrice', e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
            <div className="field"><label>Base Adults</label>
              <input type="number" min="1" value={form.baseAdults} onChange={e => set('baseAdults', Number(e.target.value))} />
            </div>
            <div className="field"><label>Max Adults</label>
              <input type="number" min="1" value={form.maxAdults} onChange={e => set('maxAdults', Number(e.target.value))} />
            </div>
            <div className="field"><label>Base Children</label>
              <input type="number" min="0" value={form.baseChildren} onChange={e => set('baseChildren', Number(e.target.value))} />
            </div>
            <div className="field"><label>No. of Rooms *</label>
              <input type="number" min="1" required value={form.totalRooms} onChange={e => set('totalRooms', Number(e.target.value))} />
            </div>
          </div>
          <button className="btn btn-gold" type="submit" disabled={saving} style={{ width: '100%', height: 44 }}>
            {saving ? '⌛ Saving…' : '✅ Save Room Type'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">🏷️ Room Types — {hotel.name}</span>
        <button className="btn btn-gold btn-sm" onClick={() => { setForm(blank); setEditing('new'); }}>+ Add Type</button>
      </div>
      <div style={{ padding: 16 }}>
        {error && (
          <div style={{ background: 'rgba(211,62,62,0.1)', color: 'var(--danger)', padding: 16, borderRadius: 12, marginBottom: 20, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>⚠️ {error}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => load()}>Retry</button>
          </div>
        )}
        {types.length === 0 && !error && <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No room types added yet.</div>}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 }}>
          {types.map(rt => (
            <div key={rt.id} style={{ background: 'var(--s1)', borderRadius: 10, padding: 16, border: '1px solid var(--border)' }}>
              <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 6 }}>{rt.name}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: 'var(--gold)', marginBottom: 8 }}>₹{(rt.basePrice || 0).toLocaleString('en-IN')}<span style={{ fontSize: 12, fontWeight: 400, color: 'var(--muted)' }}>/night</span></div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 12 }}>
                👥 {rt.baseAdults} adults (max {rt.maxAdults}) · <span style={{ color: 'var(--gold)', fontWeight: 700 }}>Inventory: {rt.totalRooms || 1} Rooms</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-teal btn-sm" style={{ flex: 1 }} onClick={() => { setForm({ ...rt }); setEditing(rt.id); }}>✏️ Edit</button>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => handleDelete(rt)}>🗑️</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
//  ROOMS TAB
// ═══════════════════════════════════════════════════════
function RoomsTab({ hotel, onSuccess }) {
  const confirm = useConfirm();
  const notify = useNotify();
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [bulkNumbers, setBulkNumbers] = useState('');
  const floorCount = hotel.floors || 3;
  const floorOptions = Array.from({ length: floorCount }, (_, i) => {
    const n = i + 1;
    const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
    return `${n}${suffix} Floor`;
  });
  const blank = { number: '', floor: floorOptions[0], roomType: '', status: 'vacant' };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    setError(null);
    try {
      const [rms, rts] = await Promise.all([
        roomsApi.list(hotel.id),
        roomTypesApi.list(hotel.id),
      ]);
      if (!Array.isArray(rms) || !Array.isArray(rts)) throw new Error('Failed to load rooms or categories');
      const roomsMapped = rms.map(r => ({
        ...r,
        roomType: r.room_type,
        hotelId: r.hotel_id
      }));
      roomsMapped.sort((a, b) => {
        const af = (a.floor || '').toString(), bf = (b.floor || '').toString();
        if (af !== bf) return af.localeCompare(bf, undefined, { numeric: true });
        return (a.number || '').toString().localeCompare((b.number || '').toString(), undefined, { numeric: true });
      });
      setRooms(roomsMapped);
      setRoomTypes(rts);
      if (!form.roomType && rts[0]) setForm(f => ({ ...f, roomType: rts[0].name }));
    } catch (err) { 
      console.error(err); 
      setError(err.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- form.roomType deliberately excluded to prevent re-loads while editing
  }, [hotel.id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const selectedType = roomTypes.find(rt => rt.name === form.roomType);
      const inventoryLimit = selectedType ? (selectedType.total_rooms || selectedType.totalRooms || 0) : 0;
      const currentRoomsOfThisType = rooms.filter(r => r.roomType === (form.roomType || 'Standard'));
      const existingCount = currentRoomsOfThisType.length;

      const numbers = bulkNumbers ? bulkNumbers.split(',').map(n => n.trim()).filter(Boolean) : [form.number];

      if (editing === 'new') {
        const potentialTotal = existingCount + numbers.length;
        if (inventoryLimit > 0 && potentialTotal > inventoryLimit) {
          throw new Error(`Limit exceeded for "${form.roomType}". You can only have ${inventoryLimit} rooms of this type (Current: ${existingCount}, Trying to add: ${numbers.length}). Please increase the limit in Room Types first.`);
        }
      }

      for (const num of numbers) {
        const data = {
          ...form,
          number: num,
          hotelId: hotel.id,
          roomType: form.roomType || 'Standard'
        };
        if (editing === 'new') {
          await roomsApi.create(data);
        } else {
          // If editing an existing room, check if type changed
          const oldRoom = rooms.find(r => r.id === editing);
          if (oldRoom && oldRoom.roomType !== form.roomType) {
            if (inventoryLimit > 0 && (existingCount + 1) > inventoryLimit) {
              throw new Error(`Limit exceeded for "${form.roomType}". You can only have ${inventoryLimit} rooms of this type.`);
            }
          }
          await roomsApi.update(editing, data);
        }
      }
      onSuccess?.(`${numbers.length} room(s) saved!`);
      setBulkNumbers('');
      setEditing(null);
      load();
    } catch (err) { await notify(err.message); }
    setSaving(false);
  };

  const handleDelete = async (rm) => {
    if (!await confirm({ icon: '🗑️', title: 'Delete Room', body: `Delete room ${rm.number}?`, confirmText: 'Delete', danger: true })) return;
    try {
      await roomsApi.delete(rm.id);
      load();
    } catch (err) { await notify(err.message); }
  };

  const statusColor = { vacant: 'var(--success)', occupied: 'var(--danger)', cleaning: 'var(--gold)', maintenance: 'var(--muted)', dirty: '#c87d3a' };

  // Group by floor
  const byFloor = {};
  rooms.forEach(r => { const f = r.floor || 'Unassigned'; if (!byFloor[f]) byFloor[f] = []; byFloor[f].push(r); });

  if (editing !== null) {
    // Determine title: if editing existing, find its current room number
    const roomTitle = editing === 'new' ? '➕ Add Room(s)' : `✏️ Room ${form.number || ''}`;

    return (
      <div className="panel">
        <div className="panel-head">
          <span className="panel-title">{roomTitle}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(null)}>← Back</button>
        </div>
        <form onSubmit={handleSave} style={{ padding: 24 }}>
          {editing === 'new' && (
            <div className="field" style={{ marginBottom: 16 }}>
              <label>Room Numbers (comma-separated for bulk)</label>
              <input value={bulkNumbers} onChange={e => setBulkNumbers(e.target.value)} placeholder="e.g. 101, 102, 103" />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>Or enter a single number below</div>
            </div>
          )}
          {!bulkNumbers && (
            <div className="field" style={{ marginBottom: 16 }}>
              <label>Room Number *</label>
              <input 
                required={!bulkNumbers} 
                value={form.number} 
                onChange={e => set('number', e.target.value)} 
                placeholder="101" 
              />
              {editing !== 'new' && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Careful: Changing the number affects the room identity.</div>}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div className="field"><label>Floor *</label>
              <select value={form.floor} onChange={e => set('floor', e.target.value)}>
                {floorOptions.map(f => <option key={f}>{f}</option>)}
              </select>
            </div>
            <div className="field"><label>Room Type *</label>
              <select value={form.roomType} onChange={e => set('roomType', e.target.value)}>
                {roomTypes.map(rt => <option key={rt.id}>{rt.name}</option>)}
              </select>
            </div>
          </div>
          {editing !== 'new' && (
            <div className="field" style={{ marginBottom: 20 }}>
              <label>Status</label>
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option value="vacant">Vacant</option>
                <option value="occupied">Occupied</option>
                <option value="cleaning">Cleaning</option>
                <option value="maintenance">Maintenance</option>
                <option value="dirty">Dirty</option>
              </select>
            </div>
          )}
          <button className="btn btn-gold" type="submit" disabled={saving} style={{ width: '100%', height: 44 }}>
            {saving ? '⌛ Saving…' : '✅ Save Room(s)'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">🛏️ Room Setup — {hotel.name}</span>
        <button className="btn btn-gold btn-sm" onClick={() => { setForm({ ...blank, roomType: roomTypes[0]?.name || '' }); setBulkNumbers(''); setEditing('new'); }}>+ Add Rooms</button>
      </div>
      <div style={{ padding: 16 }}>
        {error && (
          <div style={{ background: 'rgba(211,62,62,0.1)', color: 'var(--danger)', padding: 16, borderRadius: 12, marginBottom: 20, fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>⚠️ {error}</span>
            <button className="btn btn-sm btn-ghost" onClick={() => load()}>Retry</button>
          </div>
        )}
        {rooms.length === 0 && !error && <div style={{ textAlign: 'center', padding: 40, color: 'var(--muted)' }}>No rooms set up yet. Click + Add Rooms to begin.</div>}
        {Object.entries(byFloor).map(([floor, floorRooms]) => (
          <div key={floor} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>
              🏢 {floor} — {floorRooms.length} room{floorRooms.length !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
              {floorRooms.map(rm => (
                <div key={rm.id} style={{ background: 'var(--s1)', borderRadius: 10, padding: '12px 14px', border: `1px solid var(--border)`, borderTop: `3px solid ${statusColor[rm.status] || '#888'}` }}>
                  <div style={{ fontWeight: 900, fontSize: 18, marginBottom: 2 }}>{rm.number}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>{rm.roomType}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 10 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor[rm.status] || '#888', display: 'inline-block' }} />
                    <span style={{ fontSize: 10, color: statusColor[rm.status], fontWeight: 700, textTransform: 'capitalize' }}>{rm.status}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" style={{ flex: 1, fontSize: 11 }} onClick={() => { setForm({ ...rm }); setEditing(rm.id); }}>✏️</button>
                    <button className="btn btn-ghost btn-sm" style={{ fontSize: 11, color: 'var(--danger)' }} onClick={() => handleDelete(rm)}>🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════
//  BILL DESIGNER TAB
// ═══════════════════════════════════════════════════════
function BillDesignerTab({ hotel, onSuccess }) {
  const notify = useNotify();
  const [s, setS] = useState({
    hotelName: hotel?.name || '',
    hotelAddress: hotel?.address || '',
    hotelGst: hotel?.gstNumber || hotel?.gst_number || '',
    logoUrl: '',
    footerText: 'Thank you for choosing us! We look forward to welcoming you again.',
    termsAndConditions: '1. Standard check-out time is 11:00 AM.\n2. All bills must be settled before check-out.\n3. Management is not responsible for loss of guest property.',
    signatureLabel: 'Authorized Signatory / Seal',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    billSettingsApi.get(hotel.id).then(saved => {
      if (saved) {
        // Normalize snake_case keys from API to camelCase for the component state
        setS({
          hotelName: saved.hotel_name || saved.hotelName || hotel.name || '',
          hotelAddress: saved.hotel_address || saved.hotelAddress || hotel.address || '',
          hotelGst: saved.hotel_gst || saved.hotelGst || hotel.gstNumber || hotel.gst_number || '',
          logoUrl: saved.logo_url || saved.logoUrl || '',
          footerText: saved.footer_text || saved.footerText || 'Thank you for choosing us! We look forward to welcoming you again.',
          termsAndConditions: saved.terms_and_conditions || saved.termsAndConditions || '1. Standard check-out time is 11:00 AM.\n2. All bills must be settled before check-out.\n3. Management is not responsible for loss of guest property.',
          signatureLabel: saved.signature_label || saved.signatureLabel || 'Authorized Signatory / Seal',
        });
      } else {
        setS(prev => ({
          ...prev,
          hotelName: hotel.name || prev.hotelName,
          hotelAddress: hotel.address || prev.hotelAddress,
          hotelGst: hotel.gstNumber || hotel.gst_number || prev.hotelGst
        }));
      }
      setLoading(false);
    });
  }, [hotel.id, hotel.name, hotel.address, hotel.gstNumber, hotel.gst_number]);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const handleLogo = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 400;
        const scale = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        set('logoUrl', canvas.toDataURL('image/png', 0.8));
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(f);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await billSettingsApi.save(hotel.id, s);
      onSuccess?.('Bill design saved!');
    } catch (err) { await notify(err.message); }
    setSaving(false);
  };

  if (loading) return <div className="panel" style={{ padding: 40, textAlign: 'center' }}>Loading…</div>;

  return (
    <div className="panel">
      <div className="panel-head">
        <span className="panel-title">🖨️ Bill Designer — {hotel.name}</span>
        <button className="btn btn-gold btn-sm" disabled={saving} onClick={handleSave}>{saving ? '⌛' : '✅ Save'}</button>
      </div>
      <div style={{ padding: 24 }}>
        {/* Logo */}
        <div style={{ marginBottom: 20, background: 'var(--s1)', padding: 16, borderRadius: 10, border: '1px solid var(--border)' }}>
          <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>Hotel Logo</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {s.logoUrl
              ? <img src={s.logoUrl} alt="Logo" style={{ height: 60, maxWidth: 160, objectFit: 'contain', borderRadius: 6 }} />
              : <div style={{ width: 100, height: 60, background: 'var(--s2)', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: 'var(--muted)' }}>No logo</div>}
            <div>
              <input type="file" id="logo-upload" accept="image/*" onChange={handleLogo} style={{ display: 'none' }} />
              <label htmlFor="logo-upload" className="btn btn-ghost btn-sm" style={{ cursor: 'pointer' }}>📁 Upload Logo</label>
              {s.logoUrl && <button className="btn btn-ghost btn-sm" style={{ marginLeft: 8, color: 'var(--danger)' }} onClick={() => set('logoUrl', '')}>Remove</button>}
            </div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div className="field"><label>Hotel Name on Bill</label>
            <input value={s.hotelName} onChange={e => set('hotelName', e.target.value)} />
          </div>
          <div className="field"><label>GST Number on Bill</label>
            <input value={s.hotelGst || ''} onChange={e => set('hotelGst', e.target.value)} />
          </div>
        </div>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Hotel Address on Bill</label>
          <input value={s.hotelAddress || ''} onChange={e => set('hotelAddress', e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Footer Thank-You Message</label>
          <input value={s.footerText || ''} onChange={e => set('footerText', e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 16 }}>
          <label>Terms & Conditions (one per line)</label>
          <textarea rows={5} value={s.termsAndConditions || ''} onChange={e => set('termsAndConditions', e.target.value)}
            style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 12, padding: '10px 12px', background: 'var(--s1)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text)', width: '100%' }} />
        </div>
        <div className="field" style={{ marginBottom: 24 }}>
          <label>Signature Label</label>
          <input value={s.signatureLabel || ''} onChange={e => set('signatureLabel', e.target.value)} />
        </div>
        <button className="btn btn-gold" disabled={saving} onClick={handleSave} style={{ width: '100%', height: 46 }}>
          {saving ? '⌛ Saving…' : '✅ Save Bill Design'}
        </button>
      </div>
    </div>
  );
}
